/**
 * Parameter values that make particular Action Hub actions fail on every run,
 * found before the invocation is saved. Only the Action Hub's own actions are
 * checked: an org's action of the same name may take different parameters.
 */

import { topicNamePatternError } from "./topicNameRegex";

/** The org that owns the Action Hub's actions. */
const ACTION_HUB_OWNER = "roboto-public";

const TOPIC_NAME = "TOPIC_NAME";
const TOPIC_NAME_REGEX = "TOPIC_NAME_FROM_FILE_NAME_REGEX";

/** A parameter, and what is wrong with its value. */
export interface ParameterIssue {
  key: string;
  message: string;
}

/** A value as the action reads it: absent, or blank after trimming, is unset. */
function isSet(value: unknown): boolean {
  if (value === undefined || value === null) {
    return false;
  }
  return typeof value !== "string" || value.trim() !== "";
}

/**
 * The topic-name keys `csv_ingestion` and `parquet_ingestion` share. Both use
 * `TOPIC_NAME` when it is set and the pattern only otherwise; with neither,
 * `csv_ingestion` names the topic after the file and `parquet_ingestion`
 * fails.
 */
function topicNameIssues(
  parameters: Readonly<Record<string, unknown>>,
  fallsBackToFileName: boolean,
): ParameterIssue[] {
  if (isSet(parameters[TOPIC_NAME])) {
    return [];
  }
  const pattern = parameters[TOPIC_NAME_REGEX];
  if (!isSet(pattern)) {
    return fallsBackToFileName
      ? []
      : [
          {
            key: TOPIC_NAME,
            message: "Name the topic, or extract its name from the file name.",
          },
        ];
  }
  const error = topicNamePatternError(String(pattern));
  return error === undefined ? [] : [{ key: TOPIC_NAME_REGEX, message: error }];
}

const RULES: Readonly<
  Record<
    string,
    (parameters: Readonly<Record<string, unknown>>) => ParameterIssue[]
  >
> = {
  csv_ingestion: (parameters) => topicNameIssues(parameters, true),
  parquet_ingestion: (parameters) => topicNameIssues(parameters, false),
};

/** What is wrong with `parameters` for `action`, when it is an Action Hub action. */
export function parameterIssues(
  action: { name: string; owner: string | null },
  parameters: Readonly<Record<string, unknown>>,
): ParameterIssue[] {
  return action.owner === ACTION_HUB_OWNER && Object.hasOwn(RULES, action.name)
    ? RULES[action.name](parameters)
    : [];
}
